import { Reveal, useFeedbackModal } from "@components/ui";
import { type ReactNode } from "react";
import { useNavigate } from "react-router-dom";

/* ─────────────────────────────────────────────────────────
   Inline SVG Icons
   ───────────────────────────────────────────────────────── */

function CreditCardIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <line x1="2" y1="10" x2="22" y2="10" />
      <line x1="6" y1="15" x2="8" y2="15" />
      <line x1="11" y1="15" x2="15" y2="15" />
    </svg>
  );
}

function DocumentIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
    </svg>
  );
}

function MeterIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v6l4 2" />
    </svg>
  );
}

function TruckIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="1" y="3" width="15" height="13" />
      <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
      <circle cx="5.5" cy="18.5" r="2.5" />
      <circle cx="18.5" cy="18.5" r="2.5" />
    </svg>
  );
}

function WarningIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

/* ─────────────────────────────────────────────────────────
   Types & Sub-components
   ───────────────────────────────────────────────────────── */

interface QuickAccessCardProps {
  title: string;
  linkLabel: string;
  icon: ReactNode;
  color: string;
  onClick?: () => void;
}

function QuickAccessCard({ title, linkLabel, icon, color, onClick }: QuickAccessCardProps) {
  return (
    <div onClick={onClick} className="group w-full h-full bg-white rounded-[16px] md:rounded-[20px] p-5 md:p-6 flex flex-col gap-4 md:gap-5 border border-transparent hover:border-[#e8e9ed] hover:shadow-[0_4px_20px_rgba(0,0,0,0.08)] transition-all duration-300 cursor-pointer">
      {/* Tinted Icon */}
      <div
        className="w-11 h-11 md:w-12 md:h-12 rounded-[12px] flex items-center justify-center transition-all duration-300 group-hover:scale-105"
        style={{ backgroundColor: `${color}14`, color }}
      >
        {icon}
      </div>

      {/* Title */}
      <h3 className="text-[15px] md:text-[18px] lg:text-[20px] font-semibold text-[#1e1f24] tracking-[-0.03em] font-albert-sans leading-[1.25]">
        {title}
      </h3>

      {/* Action link */}
      <div className="mt-auto pt-1 flex items-center gap-1.5">
        <span
          className="text-[13px] md:text-[15px] font-medium tracking-[-0.02em] font-albert-sans transition-colors duration-200"
          style={{ color }}
        >
          {linkLabel}
        </span>
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300"
          style={{ color }}
        >
          <path d="M5 12H19M19 12L13 6M19 12L13 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   Data
   ───────────────────────────────────────────────────────── */

const quickAccessCards: QuickAccessCardProps[] = [
  { title: "Pay Your Gas Bill", linkLabel: "Pay Now", icon: <CreditCardIcon />, color: "#122d63" },
  { title: "Understand Your Bill", linkLabel: "View Guide", icon: <DocumentIcon />, color: "#7c3aed" },
  { title: "Submit Meter Reading", linkLabel: "Submit Now", icon: <MeterIcon />, color: "#0d9488" },
  { title: "Moving In or Out", linkLabel: "Start Transfer", icon: <TruckIcon />, color: "#2563eb" },
  { title: "Report a Gas Leak", linkLabel: "Report Now", icon: <WarningIcon />, color: "#dc2626" },
];

/* ─────────────────────────────────────────────────────────
   Main component
   ───────────────────────────────────────────────────────── */

export function QuickAccessSection() {
  const { openModal } = useFeedbackModal();
  const navigate = useNavigate();

  const cardActions: Record<string, () => void> = {
    "Pay Your Gas Bill": () => navigate("/services/pay-bill"),
    "Understand Your Bill": () => navigate("/services/pay-bill"),
    "Submit Meter Reading": () => openModal("Technical Support"),
    "Moving In or Out": () => openModal("New Connection"),
    "Report a Gas Leak": () => openModal("Emergency / Maintenance"),
  };

  return (
    <section className="w-full bg-[#f6f6f9] overflow-hidden py-12 md:py-16 lg:py-[100px] px-5 md:px-10 lg:px-[180px]">
      <div className="w-full max-w-[1368px] mx-auto font-albert-sans">
        {/* Header */}
        <Reveal direction="up">
          <div className="flex flex-col items-center gap-4 md:gap-5 mb-10 lg:mb-14">
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#122d63]/[0.08] text-[#122d63] text-[13px] md:text-[15px] font-semibold tracking-[-0.02em] uppercase">
              Quick Access
            </span>
            <h2 className="text-center text-[24px] md:text-[40px] lg:text-[56px] tracking-[-0.04em] font-semibold text-[#122d63] leading-[1.1]">
              What Do You Need Help With?
            </h2>
          </div>
        </Reveal>

        {/* Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-5">
          {quickAccessCards.map((card, index) => (
            <Reveal key={index} direction="up" delay={80 + index * 60} className="h-full">
              <QuickAccessCard
                {...card}
                onClick={cardActions[card.title]}
              />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
